import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  Save, 
  Plus, 
  Trash2, 
  RefreshCw,
  FileText,
  Filter,
  Download,
  Calendar,
  Clock
} from 'lucide-react';
import { ExportProgress } from '../components/ExportProgress';

// Operátory pro textová pole
const textOperators = {
  'equals': 'Rovná se',
  'not_equals': 'Nerovná se',
  'contains': 'Obsahuje',
  'not_contains': 'Neobsahuje',
  'starts_with': 'Začíná na',
  'ends_with': 'Končí na',
  'greater_than': 'Větší než',
  'less_than': 'Menší než',
  'greater_or_equal': 'Větší nebo rovno',
  'less_or_equal': 'Menší nebo rovno'
};

// Operátory pro datum
const dateOperators = {
  'last_days': 'Posledních X dní',
  'last_7_days': 'Posledních 7 dní',
  'last_14_days': 'Posledních 14 dní',
  'last_30_days': 'Posledních 30 dní',
  'last_90_days': 'Posledních 90 dní',
  'this_week': 'Tento týden',
  'this_month': 'Tento měsíc',
  'today': 'Dnes',
  'yesterday': 'Včera',
  'date_range': 'Rozsah dat'
};

const BalirnaAdminPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  
  // Filter settings
  const [filters, setFilters] = useState([]);
  
  // Export date range
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  
  // Export progress
  const [exportJobId, setExportJobId] = useState(null);
  const [showExportProgress, setShowExportProgress] = useState(false);
  
  // Header settings
  const [header, setHeader] = useState({
    document_name: 'Z02-S10 Balení – kontrolní list',
    version: '1',
    effective_date: '',
    revision: '',
    revision_date: ''
  });

  const canEdit = hasPagePermission(user, 'admin', 'edit');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, headerRes, columnsRes] = await Promise.all([
        api.get('/balirna/settings'),
        api.get('/balirna/header'),
        api.get('/production/available-columns')
      ]);
      
      setFilters(settingsRes.data.filters || []);
      setHeader(headerRes.data);
      setAvailableColumns(columnsRes.data.columns || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání nastavení');
    } finally {
      setLoading(false);
    }
  };

  const addFilter = () => {
    setFilters([...filters, {
      id: Date.now(),
      column: availableColumns[0] || '',
      operator: 'equals',
      value: ''
    }]);
  };

  const updateFilter = (id, updates) => {
    // Updates can be an object with multiple fields, or field + value for backward compatibility
    if (typeof updates === 'string') {
      // Old style: updateFilter(id, 'field', value) - this won't be used anymore
      return;
    }
    setFilters(prev => prev.map(f => 
      f.id === id ? { ...f, ...updates } : f
    ));
  };

  const updateFilterField = (id, field, value) => {
    setFilters(prev => prev.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const removeFilter = (id) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      await Promise.all([
        api.put('/balirna/settings', { filters }),
        api.put('/balirna/header', header)
      ]);
      
      toast.success('Nastavení uloženo');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Chyba při ukládání nastavení');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkExport = async () => {
    if (!exportDateFrom || !exportDateTo) {
      toast.error('Vyplňte prosím obě data');
      return;
    }
    
    if (exportDateFrom > exportDateTo) {
      toast.error('Datum "od" musí být před datem "do"');
      return;
    }
    
    try {
      setExporting(true);
      
      // First get the record IDs for the date range
      const recordsRes = await api.get(`/balirna/records?date_from=${exportDateFrom}&date_to=${exportDateTo}&limit=1000`);
      const recordIds = (recordsRes.data.records || []).map(r => r.id);
      
      if (recordIds.length === 0) {
        toast.error('Žádné záznamy v tomto období');
        setExporting(false);
        return;
      }
      
      // Start background export
      const response = await api.post('/balirna/records/export/bulk/start', {
        record_ids: recordIds
      });
      
      setExportJobId(response.data.job_id);
      setShowExportProgress(true);
      toast.success(`Export ${recordIds.length} záznamů spuštěn`);
    } catch (error) {
      console.error('Error starting export:', error);
      toast.error('Chyba při spuštění exportu');
    } finally {
      setExporting(false);
    }
  };

  // Funkce pro zjištění, zda je sloupec datumový
  const isDateColumn = (column) => {
    if (!column) return false;
    const lowerCol = column.toLowerCase();
    return lowerCol.includes('datum') || lowerCol.includes('date') || lowerCol.includes('čas') || lowerCol.includes('time');
  };

  // Funkce pro získání operátorů podle typu sloupce
  const getOperatorsForColumn = (column) => {
    if (isDateColumn(column)) {
      return dateOperators;
    }
    return textOperators;
  };

  // Funkce pro zjištění, zda operátor potřebuje hodnotu
  const operatorNeedsValue = (operator) => {
    const noValueOperators = ['last_7_days', 'last_14_days', 'last_30_days', 'last_90_days', 'this_week', 'this_month', 'today', 'yesterday'];
    return !noValueOperators.includes(operator);
  };

  // Funkce pro zjištění, zda operátor potřebuje dvě hodnoty (rozsah)
  const operatorNeedsRange = (operator) => {
    return operator === 'date_range';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Settings className="w-7 h-7 text-orange-600" />
              Nastavení Balírny
            </h1>
            <p className="text-slate-500 mt-1">Konfigurace filtrů a hlavičky dokumentu</p>
          </div>
          
          {canEdit && (
            <Button 
              onClick={saveSettings}
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Ukládám...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Uložit nastavení
                </>
              )}
            </Button>
          )}
        </div>

        {/* Header Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Hlavička dokumentu
            </CardTitle>
            <CardDescription>
              Nastavení hlavičky pro PDF export kontrolního listu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Název dokumentu</Label>
                <Input
                  value={header.document_name}
                  onChange={(e) => setHeader({ ...header, document_name: e.target.value })}
                  placeholder="Z02-S10 Balení – kontrolní list"
                />
              </div>
              
              <div>
                <Label>Verze č.</Label>
                <Input
                  value={header.version}
                  onChange={(e) => setHeader({ ...header, version: e.target.value })}
                  placeholder="1"
                />
              </div>
              
              <div>
                <Label>Účinný od</Label>
                <Input
                  type="date"
                  value={header.effective_date}
                  onChange={(e) => setHeader({ ...header, effective_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Revize</Label>
                <Input
                  value={header.revision}
                  onChange={(e) => setHeader({ ...header, revision: e.target.value })}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Datum revize</Label>
                <Input
                  type="date"
                  value={header.revision_date}
                  onChange={(e) => setHeader({ ...header, revision_date: e.target.value })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Export Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-orange-600" />
              Hromadný export ZIP
            </CardTitle>
            <CardDescription>
              Export kontrolních listů za zvolené období - ZIP archiv se složkami podle dnů
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Od data
                </Label>
                <Input
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex-1">
                <Label className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Do data
                </Label>
                <Input
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <Button
                onClick={handleBulkExport}
                disabled={exporting || !exportDateFrom || !exportDateTo}
                className="bg-orange-600 hover:bg-orange-700 min-w-[160px]"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generuji...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Stáhnout ZIP
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-sm text-slate-500 mt-3">
              Vygeneruje ZIP archiv se složkami podle dnů. Každý záznam bude jako samostatný PDF soubor.
            </p>
          </CardContent>
        </Card>

        {/* Filter Settings */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-orange-600" />
                  Globální filtry výrobních záznamů
                </CardTitle>
                <CardDescription>
                  Tyto filtry platí pro všechny uživatele a určují, které záznamy se zobrazí na stránce Balírna
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={addFilter}>
                <Plus className="w-4 h-4 mr-1" />
                Přidat filtr
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filters.length === 0 ? (
              <div className="text-center py-8 border rounded-lg bg-slate-50">
                <Filter className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Žádné globální filtry nastaveny</p>
                <p className="text-sm text-slate-400 mt-1">
                  Bez filtrů se zobrazí všechny výrobní záznamy všem uživatelům
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filters.map((filter, index) => {
                  const operators = getOperatorsForColumn(filter.column);
                  const needsValue = operatorNeedsValue(filter.operator);
                  const needsRange = operatorNeedsRange(filter.operator);
                  const isDateCol = isDateColumn(filter.column);
                  
                  return (
                    <div key={filter.id} className="flex flex-wrap items-end gap-3 p-4 border rounded-lg bg-slate-50">
                      <div className="flex-1 min-w-[150px]">
                        <Label className="text-xs text-slate-500">Sloupec</Label>
                        <Select
                          value={filter.column}
                          onValueChange={(v) => {
                            // Reset operator and value when column changes
                            const newOperator = isDateColumn(v) ? 'last_7_days' : 'contains';
                            updateFilter(filter.id, {
                              column: v,
                              operator: newOperator,
                              value: '',
                              value2: ''
                            });
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Vyberte sloupec" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableColumns.map(col => (
                              <SelectItem key={col} value={col}>
                                {isDateColumn(col) && <Calendar className="w-3 h-3 inline mr-1" />}
                                {col}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="w-48">
                        <Label className="text-xs text-slate-500">
                          {isDateCol ? <><Clock className="w-3 h-3 inline mr-1" />Časový rozsah</> : 'Operátor'}
                        </Label>
                        <Select
                          value={filter.operator}
                          onValueChange={(v) => {
                            const updates = { operator: v };
                            if (!operatorNeedsValue(v)) {
                              updates.value = '';
                            }
                            updateFilter(filter.id, updates);
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(operators).map(([key, label]) => (
                              <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {needsValue && !needsRange && (
                        <div className="flex-1 min-w-[120px]">
                          <Label className="text-xs text-slate-500">
                            {filter.operator === 'last_days' ? 'Počet dní' : 'Hodnota'}
                          </Label>
                          {filter.operator === 'last_days' ? (
                            <Input
                              type="number"
                              min="1"
                              value={filter.value}
                              onChange={(e) => updateFilterField(filter.id, 'value', e.target.value)}
                              placeholder="např. 7"
                              className="mt-1"
                            />
                          ) : (
                            <Input
                              value={filter.value}
                              onChange={(e) => updateFilterField(filter.id, 'value', e.target.value)}
                              placeholder="Zadejte hodnotu"
                              className="mt-1"
                            />
                          )}
                        </div>
                      )}
                      
                      {needsRange && (
                        <>
                          <div className="w-36">
                            <Label className="text-xs text-slate-500">Od data</Label>
                            <Input
                              type="date"
                              value={filter.value || ''}
                              onChange={(e) => updateFilterField(filter.id, 'value', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div className="w-36">
                            <Label className="text-xs text-slate-500">Do data</Label>
                            <Input
                              type="date"
                              value={filter.value2 || ''}
                              onChange={(e) => updateFilterField(filter.id, 'value2', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </>
                      )}
                      
                      {!needsValue && !needsRange && (
                        <div className="flex-1 min-w-[120px] flex items-center text-sm text-slate-500 italic">
                          <Clock className="w-4 h-4 mr-1" />
                          {operators[filter.operator]}
                        </div>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFilter(filter.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {filters.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Aktivní filtry:</strong> {filters.length} • 
                  Záznamy musí splňovat <strong>všechny</strong> podmínky (AND)
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Export Progress Dialog */}
      <ExportProgress
        jobId={exportJobId}
        isOpen={showExportProgress}
        onClose={() => setShowExportProgress(false)}
        title={`Export balírny ${exportDateFrom} - ${exportDateTo}`}
      />
    </Layout>
  );
};

export default BalirnaAdminPage;
